import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Upload, BadgeCheck, ShieldQuestion } from "lucide-react";

const kycStyle = {
  verified: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  unverified: "bg-secondary text-muted-foreground border-border",
};

export default function ProfileDetails() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [f, setF] = useState({ artist_name: "", real_name: "", address: "", phone: "", bio: "", avatar: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [diditBusy, setDiditBusy] = useState(false);

  useEffect(() => {
    if (user) setF({
      artist_name: user.artist_name || "", real_name: user.real_name || "", address: user.address || "",
      phone: user.phone || "", bio: user.bio || "", avatar: user.avatar || user.picture || "",
    });
  }, [user]);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadTo = async (file, cb) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    cb(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { await uploadTo(file, (u) => setF((p) => ({ ...p, avatar: u }))); } catch { toast.error("Error"); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", f); await refresh(); toast.success(t("saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const startDidit = async () => {
    setDiditBusy(true);
    try {
      const { data } = await api.post("/kyc/didit/start");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setDiditBusy(false);
    }
  };

  const checkDidit = async () => {
    setDiditBusy(true);
    try {
      const { data } = await api.get("/kyc/didit/status");
      await refresh();
      if (data.kyc_status === "verified") toast.success(t("kyc_verified"));
      else if (data.kyc_status === "rejected") toast.error(t("kyc_rejected"));
      else toast.message(t("kyc_pending"));
    } catch { toast.error("Error"); }
    finally { setDiditBusy(false); }
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("kyc") === "return") {
      checkDidit();
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = user?.kyc_status || "unverified";

  return (
    <div className="p-4 rounded-2xl border border-border bg-card mb-5" data-testid="profile-details">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("my_profile")}</p>
      <div className="flex items-center gap-4 mb-4">
        <Avatar className="w-16 h-16 border border-border">
          <AvatarImage src={f.avatar} />
          <AvatarFallback className="bg-secondary">{(f.artist_name || user?.name || "?")[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <label data-testid="avatar-upload-label" className="flex items-center gap-2 h-10 px-3 rounded-xl border border-primary/40 bg-primary/10 text-sm font-medium cursor-pointer hover:bg-primary/20 transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("avatar")}
          <input data-testid="avatar-upload-input" type="file" accept="image/*" onChange={onAvatar} className="hidden" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">{t("artist_name")}</Label><Input data-testid="pf-artist" value={f.artist_name} onChange={upd("artist_name")} className="mt-1 bg-secondary/50" /></div>
        <div><Label className="text-xs">{t("real_name")}</Label><Input data-testid="pf-real" value={f.real_name} onChange={upd("real_name")} className="mt-1 bg-secondary/50" /></div>
        <div><Label className="text-xs">{t("phone")}</Label><Input data-testid="pf-phone" value={f.phone} onChange={upd("phone")} className="mt-1 bg-secondary/50" /></div>
        <div><Label className="text-xs">{t("address")}</Label><Input data-testid="pf-address" value={f.address} onChange={upd("address")} className="mt-1 bg-secondary/50" /></div>
      </div>
      <div className="mt-3"><Label className="text-xs">{t("bio")}</Label><Textarea data-testid="pf-bio" value={f.bio} onChange={upd("bio")} rows={2} className="mt-1 bg-secondary/50" /></div>
      <Button data-testid="save-profile-btn" onClick={save} disabled={saving} className="mt-3 rounded-xl bg-primary hover:bg-primary/90 font-semibold">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save_profile")}
      </Button>

      <div className="mt-5 pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium flex items-center gap-2"><ShieldQuestion className="w-4 h-4 text-primary" /> {t("kyc")}</p>
          <Badge variant="outline" className={kycStyle[status]}>{t(`kyc_${status}`)}</Badge>
        </div>
        {status === "verified" ? (
          <p className="text-sm text-emerald-400 flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> {t("kyc_verified")}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-muted-foreground flex-1 min-w-[180px]">{t("kyc_didit_hint")}</p>
            <Button data-testid="start-didit-btn" onClick={startDidit} disabled={diditBusy} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
              {diditBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BadgeCheck className="w-4 h-4 mr-1" /> {t("verify_with_didit")}</>}
            </Button>
            {status === "pending" && (
              <Button data-testid="check-didit-btn" onClick={checkDidit} disabled={diditBusy} variant="ghost" className="rounded-xl">
                {t("check_status")}
              </Button>
            )}
          </div>
        )}
        {status === "pending" && <p className="text-xs text-amber-300 mt-2">{t("kyc_pending")}…</p>}
      </div>
    </div>
  );
}
